import { UNIQUE_EASE, CELLARS, LOOT_POWER, NIGHTMARES, type NightmareType, TOMES, GLYPH_FRAGS } from '@somnhold/content';
import { PANOPLY } from '@somnhold/content';
import { addScraps, fewestScraps } from './scraps.ts';
import type { BattleParams } from '../battle/create.ts';
import { hexDist, hexRing } from '../hex.ts';
import { createCellarsMap } from '../map.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { crowd } from './party.ts';
import { snapshot } from '../math/hash.ts';
import { deriveSeed, rngChance, rngInt, rngWeighted, seedRng } from '../math/rng.ts';
import type { BattleState, DungeonSpawn, ExpArea } from '../types.ts';
import { addRes, event, findPlayer, heroName } from './core.ts';
import { groupReached } from './eras.ts';
import { rollRelic } from './incursion.ts';
import { giveItem, lootCtx, rollItem } from './items.ts';
import { fightKit, grantXp, addGlyphFragment, addPages } from './progress.ts';
import type { CampaignState, Ms } from './types.ts';
import { tallyDeeds } from './deeds.ts';
import { tallyFeats } from './feats.ts';
import { logOuting } from './outings.ts';

/*
 * Group expeditions (docs/EXPEDICIONES.md): from the third Marea, one a day for each party size. The first is the
 * garrison cellars, for 3 or 4 heroes connected at once. Every run is laid out anew: its seed counts the runs.
 */

/** The cellars open from the third Marea. */
export const cellarsOpen = (s: CampaignState): boolean => groupReached(s, CELLARS.minGroup);

/** The party a lobby would take down: the first heroes by id, as many as the cellars hold. */
const partyOf = (s: CampaignState, pids: string[]): string[] => [...new Set(pids)].filter((pid) => !!findPlayer(s, pid)).sort().slice(0, CELLARS.maxHeroes);

/** Nightmares for one room's wave: `points` per hero, by the party's size and level. */
const fill = (rng: ReturnType<typeof seedRng>, out: DungeonSpawn[], room: number, wave: number, spots: { q: number; r: number }[], points: number, all: number, hpBp = 10000): void => {
  const beasts = CELLARS.bestiary;
  const entries = (Object.keys(beasts) as NightmareType[]).sort().map((t) => [t, beasts[t] as number] as const);
  let left = mulBp(points, all);
  let guard = 0;
  while (left > 0 && guard++ < 60) {
    const fit = entries.filter(([t]) => NIGHTMARES[t].points <= left);
    if (!fit.length) break;
    const kind = rngWeighted(rng, fit);
    left -= NIGHTMARES[kind].points;
    const at = spots[rngInt(rng, spots.length)] as { q: number; r: number };
    out.push({ room, wave, kind, tier: rngChance(rng, CELLARS.inkBp) ? 2 : 1, elite: false, q: at.q, r: at.r, ...(hpBp !== 10000 ? { hpBp } : {}) });
  }
};

/**
 * The cellars for a party of 3 or 4 live heroes. Null when they are shut (before the third Marea) or the
 * party doesn't fit. A lobby still filling is laid out for the minimum, and rebuilt as heroes arrive.
 */
export const cellarsParams = (s: CampaignState, pids: string[], day: number): BattleParams | null => {
  if (!cellarsOpen(s)) return null;
  const party = partyOf(s, pids);
  const n = imax(party.length, CELLARS.minHeroes);
  const seed = deriveSeed(s.seed, 15000 + day * 64 + ((s.expeditions?.runs ?? 0) % 64));
  const { map, areas, links } = createCellarsMap(n);
  const L = map.dungeon;
  if (!L) return null;
  const rng = seedRng(seed ^ 0x5e11);
  let lv = 0;
  for (const pid of party) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const avg = idiv(lv * 100, party.length || 1);
  // Past the level the unique armour used to come at, each level adds less: it comes later now (docs/UNICAS.md §5).
  const upTo = imin(avg, UNIQUE_EASE.fromLevel * 100);
  const level = 10000 + idiv((upTo - 100) * CELLARS.perLevelBp + imax(0, avg - upTo) * CELLARS.easedPerLevelBp, 100);
  // Past four heroes part of it comes as tougher nightmares (BIG_PARTY).
  const big = crowd(CELLARS.partyBp, n, CELLARS.big);
  const all = mulBp(big.count, level);
  // A wing is walked by one hero: its rooms are filled for one; a party past four splits two to a wing.
  const one = mulBp(CELLARS.partyBp[n > 4 ? idiv(n + 3, 4) : 1] as number, level);
  const spawns: DungeonSpawn[] = [];
  const P = CELLARS.points;
  areas.forEach((a: ExpArea, i) => {
    const room = L.rooms[i];
    if (!room || a === 'entry' || a === 'hub') return;
    // Away from the way in.
    const li = links.findIndex(([, b]) => b === a);
    const door = L.doors[li] ?? room;
    const spots: { q: number; r: number }[] = [];
    for (let rr = 1; rr <= room.radius; rr++) for (const h of hexRing(rr)) spots.push({ q: room.q + h.q, r: room.r + h.r });
    const far = spots.filter((h) => hexDist(h, door) >= 3);
    if (a === 'guard') {
      fill(rng, spawns, i, 0, far, P.guard[0] as number, all, big.hpBp);
      fill(rng, spawns, i, 1, far, P.guard[1] as number, all, big.hpBp);
      const E = CELLARS.guardElites;
      for (let k = 0; k < (E.count[n] as number); k++) {
        const at = far[rngInt(rng, far.length)] as { q: number; r: number };
        spawns.push({ room: i, wave: 1, kind: E.kind, tier: 2, elite: true, q: at.q, r: at.r, hpBp: E.hpBp });
      }
    } else if (a === 'cistern') fill(rng, spawns, i, 0, far, P.final, all, big.hpBp);
    else fill(rng, spawns, i, 0, far, a.endsWith('B') ? P.wingB : P.wingA, one);
  });
  const heroes: BattleParams['heroes'] = party.map((pid, i) => {
    const p = findPlayer(s, pid) as NonNullable<ReturnType<typeof findPlayer>>;
    return { pid, name: heroName(p), cls: p.hero.heroClass, level: p.hero.level, control: 'live', powerBp: 10000, post: i, relics: p.hero.relics, awakened: p.hero.awakened ?? [], ...fightKit(p.hero, 'expedition', s) };
  });
  return snapshot({
    mode: 'dungeon',
    seed,
    map,
    structures: [],
    heroes,
    keepHp: 1,
    keepMaxHp: 1,
    marea: s.mareaIndex,
    wave: 0,
    spawns: [],
    dungeon: { spawns, bossHp: 0, dmgBp: level },
    expedition: { kind: 'cellars', areas, links },
  });
};

/** How the gate bell looks on the keep: null until it has been brought up once. */
export const bellTrophy = (s: CampaignState): { wins: number; flawless: number; banners: boolean; sash: boolean } | null => {
  const c = s.expeditions?.cellars;
  if (!c || c.wins < 1) return null;
  return { wins: c.wins, flawless: c.flawless, banners: c.wins >= CELLARS.trophy.bannersAt, sash: c.flawless > 0 };
};

export interface ExpeditionResult {
  /** Heroes who took the day's chest. */
  rewarded: string[];
  /** Wing locks opened. */
  locks: number;
}

/**
 * A finished run of the cellars. A win: each hero's chest once a day (a rare piece, dust, XP, a shot at a relic)
 * and the bell's trophy grows (a flawless run when nobody fell). A loss: XP per lock opened.
 */
export const applyExpedition = (s: CampaignState, battle: BattleState, day: number, now: Ms): ExpeditionResult => {
  tallyDeeds(s, 'expedition', battle, now);
  tallyFeats(s, battle, {}, now);
  const x = battle.dungeon?.exp;
  const won = battle.result === 'won';
  const locks = x ? x.devices.filter((d) => d.kind === 'lock' && d.done).length : 0;
  const e = (s.expeditions ??= { runs: 0, cellars: { wins: 0, flawless: 0 } });
  e.runs++;
  if (won) {
    e.cellars.wins++;
    if (x && x.falls === 0) e.cellars.flawless++;
  }
  const rewarded: string[] = [];
  const pids = battle.heroes.map((h) => h.pid);
  for (const pid of pids) {
    const p = findPlayer(s, pid);
    if (!p) continue;
    if (won && p.expeditionDay !== day) {
      p.expeditionDay = day;
      rewarded.push(pid);
      giveItem(s, p, rollItem(s, s.rng, 'rare', lootCtx(s, p, LOOT_POWER.cellars, 'cellars', day)), now);
      grantXp(s, pid, CELLARS.reward.xp, now);
      addPages(s, pid, null, TOMES.expedition, now);
      addGlyphFragment(s, pid, 'expedition', GLYPH_FRAGS.expedition, now);
      // A scrap of the old garrison to the slot with the fewest; the player can move it once (docs/PANOPLIA.md §4).
      const to = fewestScraps(p.hero);
      if (to && addScraps(s, p, to, PANOPLY.scraps.cellars, now) > 0) p.hero.cellarsScrap = to;
      rollRelic(s, pid, CELLARS.reward.relicBp, now);
    } else grantXp(s, pid, locks * CELLARS.xpPerLock, now);
  }
  if (rewarded.length) addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: CELLARS.reward.dust * rewarded.length });
  logOuting(s, now, 'cellars', pids, won, x?.falls ?? 0);
  event(s, now, 'expedition', { data: { won, kind: 'cellars', locks, falls: x?.falls ?? 0, party: pids.join(',') } });
  s.version++;
  return { rewarded, locks };
};
